import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { downloadBlob, systemSupportAPI } from '../utils/api';
import { useToast, apiError } from '../components/Toast';
import { EmptyState, SearchInput, Badge } from '../components/layout/UI';
import { format, formatDistanceToNow } from 'date-fns';
import { Bug as BugIcon, Clock, Download, Flame, Monitor, Server, ShieldCheck, Zap } from 'lucide-react';

/**
 * frontend/src/pages/SystemSupportPage.jsx
 *
 * Errors in the TMS itself. Client products are reported through Dev Hub as
 * bugs and change requests — a different subject entirely.
 *
 * The list shows GROUPS, not occurrences. The same fault happening four
 * hundred times is one row with a count, which is the only way a list like
 * this stays readable.
 */

const SAPPHIRE = '#2E539F';
const JAVA     = '#5CC3BC';
const SUNGLOW  = '#FFC849';
const DANGER   = '#ef4444';

const SEVERITY = {
  Critical: { fg: DANGER,    bg: 'rgba(239,68,68,.10)' },
  High:     { fg: '#8A6400', bg: 'rgba(255,200,73,.20)' },
  Medium:   { fg: '#2f8a84', bg: 'rgba(92,195,188,.16)' },
  Low:      { fg: SAPPHIRE,  bg: 'rgba(46,83,159,.10)' },
};

const OPEN_STATUSES = ['New', 'Regressed', 'Investigating', 'Confirmed', 'In Progress'];
const FIXED_STATUSES = ['Resolved', 'Verified', 'Closed'];
const ALL_STATUSES  = [...OPEN_STATUSES, 'Resolved', 'Verified', 'Closed', 'Ignored', 'Duplicate'];

const KIND_ICON = {
  react_render: Monitor, window_error: Monitor, unhandled_rejection: Zap,
  api: Server, server: Server,
};
const KIND_LABEL = {
  react_render: 'Page crash', window_error: 'Browser error',
  unhandled_rejection: 'Unhandled promise', api: 'API failure', server: 'Server error',
};

export default function SystemSupportPage() {
  const toast = useToast();
  const [groups,  setGroups]  = useState([]);
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  const [status, setStatus] = useState('open');   // 'open' is a view, not a stored value
  const [environment, setEnvironment] = useState('');
  const [severity, setSeverity] = useState('');
  const [search, setSearch] = useState('');

  const [exporting, setExporting] = useState(false);

  /**
   * Exports whatever the filters currently show, not everything. A report of
   * "open critical errors in production" is the useful artefact; a dump of
   * every row ever recorded is not.
   */
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await systemSupportAPI.exportXlsx({
        environment: environment || undefined,
        severity:    severity || undefined,
        status:      ALL_STATUSES.includes(status) ? status : undefined,
      });
      downloadBlob(r.data, `system-support-${new Date().toISOString().slice(0,10)}.xlsx`);
    } catch (e) {
      toast.error(apiError(e, 'Could not build the spreadsheet.'));
    } finally { setExporting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        environment: environment || undefined,
        severity: severity || undefined,
        search: search || undefined,
        // 'open' is several statuses, so it is filtered client-side below
        // rather than sent — one round trip instead of four.
        status: ['open', ''].includes(status) ? undefined : status,
      };
      const [g, s] = await Promise.all([
        systemSupportAPI.getAll(params),
        systemSupportAPI.getStats(),
      ]);
      setGroups(g.data || []);
      setStats(s.data || null);
    } catch (e) {
      toast.error(apiError(e, 'Could not load System Support.'));
    } finally {
      setLoading(false);
    }
  }, [environment, severity, search, status, toast]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  /**
   * Three views, then any single status.
   *
   * Fixed is separate because a resolved error sitting in one list with
   * everything else is the confusion you described — you cannot tell at a
   * glance what has been dealt with. A recurrence of a fixed error is
   * 'Regressed', so it leaves Fixed and reappears under Open where it belongs.
   */
  const visible =
      status === 'open'  ? groups.filter(g => OPEN_STATUSES.includes(g.status))
    : status === 'fixed' ? groups.filter(g => FIXED_STATUSES.includes(g.status))
    : status === ''      ? groups
    : groups.filter(g => g.status === status);

  const cards = stats ? [
    { label: 'New',          value: stats.new_errors, icon: Flame,    tone: stats.new_errors   ? SUNGLOW : JAVA },
    { label: 'Critical',     value: stats.critical,   icon: BugIcon,  tone: stats.critical     ? DANGER  : JAVA },
    { label: 'Unresolved',   value: stats.unresolved, icon: ShieldCheck, tone: SAPPHIRE },
    { label: 'Last 24 hours',value: stats.last_24h,   icon: Clock,    tone: SAPPHIRE },
  ] : [];

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
        <h1 className="page-title">System Support</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
          Errors detected in the TMS itself. This page does not cover LMS, LOS
          or other Q-Lana products — those are reported in Dev Hub.
        </p>
      </div>
        <button onClick={exportXlsx} disabled={exporting}
          className="btn-secondary text-sm flex items-center gap-1.5 flex-shrink-0">
          <Download size={14} /> {exporting ? 'Building…' : 'Export'}
        </button>
      </div>

      {/* ── The four figures a developer actually asks for ───────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="stat-card">
              <div className="flex items-start justify-between">
                <span className="stat-label">{c.label}</span>
                <Icon size={15} style={{ color: c.tone }} />
              </div>
              <p className="text-3xl font-bold" style={{ color: 'var(--text)' }}>{c.value ?? 0}</p>
            </div>
          );
        })}
      </div>

      {/* ── Where it is going wrong ──────────────────────────────────────── */}
      {stats?.by_module?.length > 0 && (
        <div className="card">
          <h3 className="font-semibold text-sm mb-1" style={{ color: 'var(--text)' }}>
            Where errors are coming from
          </h3>
          <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
            Unresolved only, by total occurrences
          </p>
          <div className="space-y-2">
            {stats.by_module.map(m => {
              const max = Math.max(1, ...stats.by_module.map(x => x.occurrences || 0));
              return (
                <div key={m.module}>
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: 'var(--text)' }}>{m.module}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {m.occurrences} from {m.n} error{m.n === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                    <div className="h-full rounded-full"
                      style={{ width: `${((m.occurrences || 0) / max) * 100}%`, background: SAPPHIRE }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 items-center">
        <SearchInput value={search} onChange={setSearch} placeholder="Search error messages…" />
        {/* Tabs rather than a dropdown. "Fixed" is the one you asked for: a
            resolved error should be visibly resolved, not buried in a list
            where it looks like everything else. */}
        <div className="flex rounded-lg border overflow-hidden text-sm self-start"
          style={{ borderColor:'var(--border)' }}>
          {[['open','Open'],['fixed','Fixed'],['',	'All']].map(([v,l]) => (
            <button key={v || 'all'} onClick={() => setStatus(v)}
              className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1.5 ${status===v ? 'bg-brand-500 text-white' : ''}`}
              style={status!==v ? { color:'var(--text-secondary)', background:'var(--surface)' } : {}}>
              {l}
              {status===v && <span className="font-bold">{visible.length}</span>}
            </button>
          ))}
        </div>
        <select className="select w-44 text-sm" value={ALL_STATUSES.includes(status) ? status : ''}
          onChange={e => setStatus(e.target.value)}>
          <option value="">Any single status</option>
          {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="select w-40 text-sm" value={severity} onChange={e => setSeverity(e.target.value)}>
          <option value="">Any severity</option>
          {Object.keys(SEVERITY).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="select w-44 text-sm" value={environment} onChange={e => setEnvironment(e.target.value)}>
          <option value="">Any environment</option>
          {['Production', 'Staging', 'UAT', 'Development'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* ── The list ─────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState icon={ShieldCheck}
          title={status === 'open' ? 'No open errors' : 'Nothing matches those filters'}
          message={status === 'open'
            ? 'Nothing is currently going wrong in the TMS that has not been dealt with.'
            : 'Try widening the filters.'} />
      ) : (
        <div className="card p-0 divide-y" style={{ borderColor: 'var(--border)' }}>
          {visible.map(g => {
            const sev = SEVERITY[g.severity] || SEVERITY.Medium;
            const Icon = KIND_ICON[g.kind] || BugIcon;
            return (
              <Link key={g.id} to={`/system-support/${g.id}`}
                className="flex items-start gap-3 px-4 py-3 hover:bg-black/[.02] transition-colors">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: sev.bg, color: sev.fg }}>
                  <Icon size={15} />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate" style={{ color: 'var(--text)' }}>
                    {g.title}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                    {KIND_LABEL[g.kind] || g.kind}
                    {g.module ? ` · ${g.module}` : ''}
                    {' · '}
                    {g.last_seen ? `last ${formatDistanceToNow(new Date(g.last_seen), { addSuffix: true })}` : ''}
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {g.task_id && (
                    <span className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: 'rgba(92,195,188,.16)', color: '#2f8a84' }}>
                      task linked
                    </span>
                  )}
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                    style={{ background: sev.bg, color: sev.fg }}>
                    {g.severity}
                  </span>
                  <Badge value={g.status} type="status" />
                  <div className="text-right w-20">
                    <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>{g.occurrence_count}</p>
                    <p className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                      {g.affected_users > 0 ? `${g.affected_users} user${g.affected_users === 1 ? '' : 's'}` : 'times'}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {visible.length > 0 && (
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          Each row is one distinct problem. The number on the right is how many
          times it has happened, not how many rows there are.
        </p>
      )}
    </div>
  );
}
